import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Search, Home, ShoppingBag, Globe, Plane, Info, 
  ShoppingCart, User, Menu, X, Store, Package, ChevronRight,
  Cookie, Croissant, Flame, Shirt, Palette, Gem, Lamp, Sparkles, Leaf,
  Image as ImageIcon, Coffee, Gift, Heart, Music, BookOpen, Phone, HelpCircle,
  FileText, Shield, RefreshCw, Settings, LogOut, UserCircle, ClipboardList, ChevronDown
} from 'lucide-react';

// Icon mapping for categories
const ICON_MAP = {
  'cookie': Cookie, 'croissant': Croissant, 'flame': Flame, 'shirt': Shirt,
  'palette': Palette, 'gem': Gem, 'lamp': Lamp, 'sparkles': Sparkles,
  'leaf': Leaf, 'image': ImageIcon, 'coffee': Coffee, 'nut': Package,
  'gift': Gift, 'package': Package, 'heart': Heart, 'star': Store,
  'shopping-bag': ShoppingBag, 'home': Home, 'music': Music, 'book-open': BookOpen,
};

// Traditional art SVG decorations
const TempleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
    <path d="M12 2L2 8v2h2v10h6v-6h4v6h6V10h2V8L12 2zm0 2.5L18 8H6l6-3.5zM6 10h12v8h-2v-6H8v6H6v-8z"/>
  </svg>
);

const DivaLamp = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full opacity-20">
    <path d="M12 2c-.5 0-1 .2-1.4.6L8 5v2c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V5l-2.6-2.4C13 2.2 12.5 2 12 2zm-2 8v6c0 1.1.9 2 2 2s2-.9 2-2v-6h-4zm-4 8v2h12v-2H6z"/>
  </svg>
);

const PaisleyPattern = () => (
  <svg viewBox="0 0 100 100" className="w-full h-full opacity-5">
    <path d="M50 10c-20 0-35 15-35 35s15 35 35 35c15 0 25-10 25-25 0-10-5-20-15-25 5-5 10-12 10-20 0-5-10-0-20 0z" fill="currentColor"/>
  </svg>
);

const KolamDot = ({ className }) => (
  <div className={`w-1.5 h-1.5 rounded-full bg-current opacity-20 ${className}`} />
);

export default function CustomerLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { getCartItemsCount } = useCart();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const cartCount = getCartItemsCount();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/collections`);
        const sorted = (response.data || []).sort((a, b) => 
          (a.display_order || 0) - (b.display_order || 0)
        );
        setCategories(sorted);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [API_URL]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSidebarOpen(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FAFDF8] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        {/* Main Header */}
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
                alt="NammaHut" 
                className="h-10 w-10"
              />
              <div>
                <span className="text-xl font-bold text-emerald-700">nammahut</span>
                <span className="block text-[10px] text-gray-500 tracking-wide">TRADITIONAL MARKETPLACE</span>
              </div>
            </Link>

            {/* Search - Desktop */}
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:block">
              <div className="flex border border-gray-300 rounded-lg overflow-hidden focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for products..."
                  className="flex-1 px-4 py-2.5 text-sm outline-none"
                />
                <button type="submit" className="px-5 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </form>

            {/* Right Actions */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <Link to="/vendor/register" className="hidden lg:block text-sm text-emerald-700 hover:text-emerald-800 font-medium">
                Become a Seller
              </Link>

              {isAuthenticated ? (
                <div className="relative group">
                  <button className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700">
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline">{user?.name?.split(' ')[0] || 'Account'}</span>
                  </button>
                  <div className="absolute right-0 top-full pt-2 w-48 hidden group-hover:block">
                    <div className="bg-white rounded-lg shadow-lg border py-2">
                      <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">My Orders</Link>
                      <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">My Profile</Link>
                      <button onClick={() => { logout(); navigate('/'); }} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">Sign Out</button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700">
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">Sign In</span>
                </Link>
              )}

              <button onClick={() => navigate('/cart')} className="relative">
                <ShoppingCart className="w-6 h-6 text-gray-700" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-emerald-600 text-white text-xs font-bold rounded-full">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          <form onSubmit={handleSearch} className="mt-3 md:hidden">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="flex-1 px-4 py-2 text-sm outline-none"
              />
              <button type="submit" className="px-4 bg-emerald-600 text-white">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>

        {/* Category Nav - Light Background */}
        <div className="border-b border-gray-200 hidden md:block">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center space-x-6 text-sm">
              <button onClick={() => setSidebarOpen(true)} className="text-gray-700 hover:text-emerald-700 flex items-center space-x-1">
                <Menu size={16} /> <span>All</span>
              </button>
              <Link to="/products" className="text-gray-700 hover:text-emerald-700">Products</Link>
              <Link to="/international" className="text-gray-700 hover:text-emerald-700">International</Link>
              <Link to="/nammayatra" className="text-gray-700 hover:text-emerald-700">NammaYatra</Link>
              {categories.slice(0, 4).map((cat) => (
                <Link 
                  key={cat._id || cat.id}
                  to={`/products?category=${encodeURIComponent(cat.name)}`} 
                  className="text-gray-500 hover:text-emerald-700"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-80 max-w-[85vw] bg-[#0F5132] h-full overflow-y-auto shadow-2xl flex flex-col">
            {/* Simple gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent pointer-events-none"></div>

            {/* Sidebar Header */}
            <div className="relative p-5 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-xl shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
                      alt="nammahut" 
                      className="h-10 w-10 object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">nammahut</h2>
                    <p className="text-xs text-[#90EE90] tracking-wider">TRADITIONAL MARKETPLACE</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSidebarOpen(false)} 
                  className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="mt-4 md:hidden">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F5132]" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-white text-[#0F5132] placeholder:text-[#0F5132]/50"
                  />
                </div>
              </form>
            </div>

            {/* Main Menu */}
            <div className="flex-1 py-4 relative">
              <nav className="space-y-1 px-3">
                <SidebarLink to="/" icon={Home} label="Home" active={isActive('/')} onClick={() => setSidebarOpen(false)} />
                <SidebarLink to="/products" icon={ShoppingBag} label="All Products" active={isActive('/products')} onClick={() => setSidebarOpen(false)} />
                
                <div className="my-3 mx-3 border-t border-white/10"></div>
                
                <Link
                  to="/international"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 mx-1 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg"
                >
                  <Globe className="w-5 h-5" />
                  <span className="font-medium">International Delivery</span>
                  <span className="ml-auto">🌍</span>
                </Link>
                
                <SidebarLink to="/nammayatra" icon={Plane} label="NammaYatra" active={isActive('/nammayatra')} onClick={() => setSidebarOpen(false)} />
              </nav>

              {/* Categories */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="px-6 mb-3 text-xs font-bold text-[#90EE90] uppercase tracking-widest flex items-center gap-2">
                  <span className="w-8 h-px bg-[#90EE90]/30"></span>
                  Categories
                  <span className="flex-1 h-px bg-[#90EE90]/30"></span>
                </p>
                <nav className="space-y-0.5 px-3">
                  {categories.map((category) => {
                    const IconComponent = ICON_MAP[category.icon] || Package;
                    return (
                      <Link
                        key={category.id || category._id}
                        to={`/products?category=${encodeURIComponent(category.name)}`}
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 mx-1 rounded-xl hover:bg-white/10 text-white/90 transition-all group"
                      >
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                          <IconComponent className="w-4 h-4 text-[#90EE90]" />
                        </div>
                        <span className="text-sm font-medium group-hover:text-white">{category.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Support */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="px-6 mb-3 text-xs font-bold text-[#90EE90] uppercase tracking-widest flex items-center gap-2">
                  <span className="w-8 h-px bg-[#90EE90]/30"></span>
                  Support
                  <span className="flex-1 h-px bg-[#90EE90]/30"></span>
                </p>
                <nav className="space-y-0.5 px-3">
                  <SidebarLink to="/about" icon={Info} label="About Us" onClick={() => setSidebarOpen(false)} />
                  <SidebarLink to="/contact" icon={Phone} label="Contact Us" onClick={() => setSidebarOpen(false)} />
                  <SidebarLink to="/help" icon={HelpCircle} label="Help & FAQ" onClick={() => setSidebarOpen(false)} />
                </nav>
              </div>

              {/* Policies */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="px-6 mb-3 text-xs font-bold text-[#90EE90] uppercase tracking-widest flex items-center gap-2">
                  <span className="w-8 h-px bg-[#90EE90]/30"></span>
                  Policies
                  <span className="flex-1 h-px bg-[#90EE90]/30"></span>
                </p>
                <nav className="space-y-0.5 px-3">
                  <SidebarLink to="/terms" icon={FileText} label="Terms & Conditions" onClick={() => setSidebarOpen(false)} />
                  <SidebarLink to="/privacy" icon={Shield} label="Privacy Policy" onClick={() => setSidebarOpen(false)} />
                  <SidebarLink to="/refund" icon={RefreshCw} label="Refund Policy" onClick={() => setSidebarOpen(false)} />
                </nav>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="relative p-4 border-t border-white/10">
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <Store className="w-5 h-5 text-[#0F5132]" />
                  </div>
                  <div>
                    <span className="font-bold text-white block">Become a Seller</span>
                    <span className="text-xs text-[#90EE90]">Start your business</span>
                  </div>
                </div>
                <Button 
                  onClick={() => { navigate('/vendor/register'); setSidebarOpen(false); }}
                  className="w-full bg-white hover:bg-gray-100 text-[#0F5132] rounded-lg font-semibold"
                  size="sm"
                >
                  Register Now
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0F5132] text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-20 h-20 text-white/5">
            <PaisleyPattern />
          </div>
          <div className="absolute bottom-10 right-10 w-24 h-24 text-white/5 rotate-90">
            <PaisleyPattern />
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-12 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
                  alt="nammahut" 
                  className="h-10 w-10 bg-white rounded-lg p-1"
                />
                <div>
                  <span className="font-bold text-white">nammahut</span>
                  <p className="text-[10px] text-[#90EE90]">TRADITIONAL MARKETPLACE</p>
                </div>
              </div>
              <p className="text-sm text-white/70">Celebrating India&apos;s rich heritage through authentic traditional products.</p>
            </div>
            <div>
              <h4 className="font-bold text-[#90EE90] mb-4 text-sm tracking-wider">QUICK LINKS</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/products" className="hover:text-white transition-colors">All Products</Link></li>
                <li><Link to="/international" className="hover:text-white transition-colors">International</Link></li>
                <li><Link to="/nammayatra" className="hover:text-white transition-colors">NammaYatra</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#90EE90] mb-4 text-sm tracking-wider">SUPPORT</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/help" className="hover:text-white transition-colors">Help & FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#90EE90] mb-4 text-sm tracking-wider">LEGAL</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-6 text-center text-sm text-white/50">
            © 2025 nammahut. Preserving traditions, one product at a time.
          </div>
        </div>
      </footer>
    </div>
  );
}

// Sidebar Link Component
function SidebarLink({ to, icon: Icon, label, active, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 mx-1 rounded-xl transition-all ${
        active 
          ? 'bg-white/20 text-white font-semibold' 
          : 'hover:bg-white/10 text-white/80 hover:text-white'
      }`}
    >
      <Icon className={`w-5 h-5 ${active ? 'text-[#90EE90]' : 'text-white/70'}`} />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}
